import React from 'react';
import PropTypes from 'prop-types';
import Callout from 'components/tools/Callout';
import Record from './Record';
import debounce from 'lodash/debounce';
import {BranchSelect, Checkbox} from 'components/forms';
import {FormattedMessage, intlShape, injectIntl} from 'react-intl';
import {Loader, Card, ToolTip} from 'components';
import {PageFooter, Pagination, ConfirmationModal, Table} from 'components/layout';
import {events} from 'lib';
import {getUnverifiedAddresses, verifyAddress, attemptedVerification} from 'api/dataCleanup';
import Dealer from 'models/dealer';

function formatAddress(cust) {
  return [cust.address1, cust.address2, cust.city, cust.state, cust.zip].filter(x => !!x).join(', ');
}

class Page extends React.Component {
  static propTypes = {
    intl: intlShape.isRequired,
    dealer: PropTypes.instanceOf(Dealer).isRequired,
  };

  state = {
    loading: true,
    results: [],
    toSave: null,
    total: 0,
    page: 1,
    currentSort: 'lastAnalysisDate',
    sortDirection: 'desc',
  };

  componentWillMount() {
    this.fetch();

    this.confirmSave = debounce(this._confirmSave, 500, {leading: true, trailing: false});
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.page !== this.state.page || prevState.currentSort !== this.state.currentSort || prevState.sortDirection !== this.state.sortDirection) {
      this.fetch();
    }
  }

  onPageChange = (page) => {
    this.setState({page});
  };

  setAttempted = (id) => {
    attemptedVerification(id);
  };

  _confirmSave = () => {
    let a = this.state.toSave.addressObject;
    let address = {
      Address1: a.address1,
      Address2: a.address2,
      City: a.city,
      State: a.state,
      Zip: a.zip,
      Country: a.country,
      AddressCompleteID: a.addressCompleteID,
    };

    let id = this.state.toSave.id;
    let results = this.state.results;
    let record = results.find(x => x.id === id);
    let {formatMessage} = this.props.intl;

    verifyAddress(address, id)
      .then(() => {
        events.publish('success.user', {
          key: 'verify-address-success',
          message: <span><FormattedMessage id="tools.address.saved"/> <Callout href={'#customer/' + id}>
            {record && record.name ? record.name : 'Customer'}
          </Callout> - <Callout href={'#customer/' + id}>{formatAddress(a)}</Callout>
          </span>,
        });
        this.setState({
          results: results.filter(x => x.id !== id),
          toSave: null,
        });
      })
      .catch(err => {
        console.error(err);
      });
  };

  cancelSave = () => {
    this.setState({
      toSave: null,
    });
  };

  fetch = () => {
    const data = {
      branches: this.branches ? this.branches.getWrappedInstance().getValue() : null,
      sort: this.state.currentSort,
      sortDirection: this.state.sortDirection,
      excludeAttempted: this.excludeAttempted ? !this.excludeAttempted.getChecked() : true,
      showInactive: this.showInactive ? this.showInactive.getChecked() : undefined,
      page: this.state.page || 1,
    };

    if (data.branches) {
      data.branches = data.branches.join(',');
    }

    this.setState({
      loading: true,
    });

    getUnverifiedAddresses(data)
      .then(x => {
        this.setState({
          results: x.failed,
          loading: false,
          total: x.total,
        });
      });
  };

  save = (id, obj) => {
    this.setState({
      toSave: {
        id: id,
        addressObject: obj,
      },
    });
  };

  sort = (key) => {
    if (key !== this.state.currentSort) {
      this.setState({
        currentSort: key,
      });
    } else {
      this.setState({
        sortDirection: this.state.sortDirection === 'asc' ? 'desc' : 'asc',
      });
    }
  };

  render() {
    let {formatMessage} = this.props.intl;
    let headings = [
      {label: formatMessage({id: 'general.account'}), sortKey: 'accountNo'},
      {label: formatMessage({id: 'general.last.analysis'}), sortKey: 'lastAnalysisDate'},
      {label: formatMessage({id: 'general.customer'}), sortKey: 'name'},
      {
        label: <ToolTip iconOnly
                        invert
                        title={
                          <span><i className='bl bl-attention blue'/> <FormattedMessage id="tools.verify.unverifiable.tooltip"/></span>}
                        topRight/>,
      },
      {label: formatMessage({id: 'general.address'}), sortKey: 'address1'},
      {label: formatMessage({id: 'general.verify'}), sortKey: 'addressVerified'},
    ];

    let results = this.state.results;

    let rows = results.map((x, i) =>
      <Record name={x.name}
              id={x.id}
              address={formatAddress(x)}
              i={i}
              key={x.id}
              dealer={this.props.dealer}
              saveAddress={this.save}
              initialAddress={x}
              setAttempted={this.setAttempted}
              previouslyAttempted={x.addressVerified === 2}
              accountNumber={x.accountNo}
              lastAnalysis={x.lastAnalysisDate}/>
    );

    return <div>
      <ConfirmationModal visible={this.state.toSave !== null}
                         message={formatMessage({id: 'tools.verify.confirm.address'})}
                         onClose={this.cancelSave}
                         dismissKey='DISMISS_CONFIRM_VERIFY_ADDRESS'
                         submitLabel={formatMessage({id: 'general.confirm'})}
                         onSubmit={this.confirmSave}/>
      <Card fullWidth>
        <div className='row'>
          <div className='col-md-3'>
            <BranchSelect label={null}
                          ref={b => this.branches = b}
                          onChange={this.fetch}
                          style={{marginBottom: 0}}/>
          </div>
          <div className='col-md-5'>
            <Checkbox label={formatMessage({id: 'tools.verify.include.address'})}
                      ref={e => this.excludeAttempted = e}
                      dataChanged={this.fetch}/>
          </div>
          <div className='col-md-4'>
            <Checkbox label={formatMessage({id: 'tools.verify.include.customers'})}
                      dataChanged={this.fetch}
                      ref={si => this.showInactive = si}/>
          </div>
          <div className='col-md-12 semi-bold'>
            <i className='bl bl-help-circle blue fa-lg'/>
            <Callout>{this.state.total}</Callout> <FormattedMessage id="tools.verify.address.verified"/>
          </div>
        </div>
      </Card>

      <Loader loaded={!this.state.loading}>
        <Table headings={headings}
               rows={rows}
               sort={this.sort}
               currentSort={this.state.currentSort}
               sortDirection={this.state.sortDirection}/>
        <PageFooter className='tc'>
          <Pagination onPageChange={this.onPageChange}
                      page={this.state.page}
                      count={25}
                      total={this.state.total}/>
        </PageFooter>
      </Loader>
    </div>;
  }
}

export default injectIntl(Page);
